import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Users, FileText, MessageSquare, CreditCard, Eye, EyeOff, Star, StarOff, CheckCircle, XCircle, ExternalLink, RefreshCw, Shield, Download, Search, Pencil, Square, CheckSquare, LogIn, Send, ArrowUp, ArrowDown, Mail, Play, Clock } from "lucide-react";

const TABS = [
  { id: "users", label: "USERS", icon: Users },
  { id: "nurture", label: "NURTURE", icon: Mail },
  { id: "profiles", label: "PROFILES", icon: FileText },
  { id: "scouts", label: "SCOUTS", icon: Shield },
  { id: "enquiries", label: "ENQUIRIES", icon: MessageSquare },
  { id: "payments", label: "PAYMENTS", icon: CreditCard },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [payments, setPayments] = useState([]);
  const [scoutApps, setScoutApps] = useState([]);
  const [nurtureStats, setNurtureStats] = useState(null);
  const [nurtureRunning, setNurtureRunning] = useState(false);
  const [paymentStats, setPaymentStats] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [loading, setLoading] = useState(true);

  // Search/filter state
  const [userSearch, setUserSearch] = useState("");
  const [userRole, setUserRole] = useState("");
  const [userPlan, setUserPlan] = useState("");
  const [userCountry, setUserCountry] = useState("");
  const [userSortBy, setUserSortBy] = useState("created_at");
  const [userSortDir, setUserSortDir] = useState("desc");
  const [profileSearch, setProfileSearch] = useState("");
  const [profileStatus, setProfileStatus] = useState("");
  const [scoutSearch, setScoutSearch] = useState("");
  const [countries, setCountries] = useState([]);
  const [blastSending, setBlastSending] = useState(false);
  const { user: currentAdmin, refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/countries"); setCountries(data.countries || []); } catch {}
    })();
  }, []);

  // Bulk selection
  const [selected, setSelected] = useState(new Set());

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "users") {
        const params = new URLSearchParams();
        if (userSearch) params.append("q", userSearch);
        if (userRole) params.append("role", userRole);
        if (userPlan) params.append("plan", userPlan);
        if (userCountry) params.append("country", userCountry);
        if (userSortBy) params.append("sort_by", userSortBy);
        if (userSortDir) params.append("sort_dir", userSortDir);
        const { data } = await api.get(`/admin/users?${params}`);
        setUsers(data.users || []);
      } else if (tab === "profiles") {
        const params = new URLSearchParams();
        if (profileSearch) params.append("q", profileSearch);
        if (profileStatus) params.append("status", profileStatus);
        const { data } = await api.get(`/admin/profiles?${params}`);
        setProfiles(data.profiles || []);
      } else if (tab === "enquiries") {
        const { data } = await api.get("/admin/enquiries");
        setEnquiries(data.enquiries || []);
      } else if (tab === "scouts") {
        const { data } = await api.get("/admin/scout-applications");
        setScoutApps(data.applications || []);
      } else if (tab === "payments") {
        const { data } = await api.get("/admin/payments");
        setPayments(data.transactions || []);
        setPaymentStats(data.stats || null);
      } else if (tab === "nurture") {
        const { data } = await api.get("/admin/nurture/stats");
        setNurtureStats(data);
      }
    } catch { toast.error("Failed to load data"); }
    setLoading(false);
    setSelected(new Set());
  }, [tab, userSearch, userRole, userPlan, userCountry, userSortBy, userSortDir, profileSearch, profileStatus]);

  useEffect(() => { loadData(); }, [loadData]);

  const adminAction = async (action, userId) => {
    try {
      await api.post(`/admin/profiles/${userId}/${action}`);
      toast.success(`${action} completed`);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const changeUserPlan = async (userId, plan) => {
    try {
      await api.put(`/admin/users/${userId}/plan`, { plan });
      toast.success(`Plan changed`);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const impersonateUser = async (userId, email) => {
    if (!window.confirm(`Log in as ${email}? Your admin session will be preserved and you can exit anytime via the red banner.`)) return;
    try {
      const { data } = await api.post("/admin/impersonate", { user_id: userId });
      toast.success(`Impersonating ${data.target?.email || email}`);
      if (refreshUser) await refreshUser();
      navigate("/dashboard");
    } catch (e) { toast.error(e.response?.data?.detail || "Impersonation failed"); }
  };

  const exportUsersCsv = async () => {
    const params = new URLSearchParams();
    if (userSearch) params.append("q", userSearch);
    if (userRole) params.append("role", userRole);
    if (userPlan) params.append("plan", userPlan);
    if (userCountry) params.append("country", userCountry);
    try {
      const res = await api.get(`/admin/users/export.csv?${params}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "esn_users.csv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("CSV export failed"); }
  };

  const sendBlast = async () => {
    const count = users.filter((u) => u.role === "player" && !u.plan).length;
    if (!window.confirm(`Send "We've made it better" announcement to ALL unpaid players? This will email ~${count}+ users. Continue?`)) return;
    setBlastSending(true);
    try {
      const { data } = await api.post("/admin/blast/we-made-it-better");
      toast.success(`Blast complete — sent ${data.sent} · skipped ${data.skipped}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Blast failed"); }
    setBlastSending(false);
  };

  const toggleSort = (col) => {
    if (userSortBy === col) {
      setUserSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setUserSortBy(col); setUserSortDir("desc");
    }
  };

  const SortHeader = ({ col, children }) => (
    <button onClick={() => toggleSort(col)} className="flex items-center gap-1 hover:text-[#D4AF37] transition-colors" data-testid={`sort-${col}`}>
      <span>{children}</span>
      {userSortBy === col && (userSortDir === "desc" ? <ArrowDown size={11} className="text-[#D4AF37]" /> : <ArrowUp size={11} className="text-[#D4AF37]" />)}
    </button>
  );

  const runNurtureNow = async () => {
    setNurtureRunning(true);
    try {
      const { data } = await api.post("/admin/nurture/run-now");
      toast.success(`Tick complete — ${data.sent} email(s) sent`);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Run failed"); }
    setNurtureRunning(false);
  };

  const reconcilePayments = async () => {
    if (!window.confirm("Query Stripe for every pending payment and update local status?\n\nThis recovers payments that completed on Stripe but never updated locally (because the user closed the browser before the welcome page).")) return;
    setReconciling(true);
    try {
      const { data } = await api.post("/admin/payments/reconcile");
      const msg = `Checked ${data.checked} pending · recovered ${data.recovered_paid} paid · expired ${data.marked_expired} · still pending ${data.still_pending}${data.errors ? ` · errors ${data.errors}` : ""}`;
      toast.success(msg);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Reconcile failed"); }
    setReconciling(false);
  };

  const fmtRelative = (iso) => {
    if (!iso) return "-";
    const ms = new Date(iso).getTime() - Date.now();
    const abs = Math.abs(ms);
    const mins = Math.round(abs / 60000);
    const hrs = Math.round(abs / 3600000);
    const days = Math.round(abs / 86400000);
    const future = ms > 0;
    if (mins < 60) return future ? `in ${mins}m` : `${mins}m ago`;
    if (hrs < 48) return future ? `in ${hrs}h` : `${hrs}h ago`;
    return future ? `in ${days}d` : `${days}d ago`;
  };

  const STEP_LABELS = ["Day 1 — Invisible", "Day 3 — Real player", "Day 7 — At risk", "Day 14 — Insight", "Day 21 — Final"];

  function KpiCard({ label, value, hint, testId }) {
    return (
      <div className="bg-[#0E0E0E] border border-[#1A1A1A] p-4" data-testid={testId}>
        <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#666] mb-2">{label}</p>
        <p className="text-2xl sm:text-3xl font-black tracking-tight text-[#D4AF37] tabular-nums">{value}</p>
        <p className="text-[10px] text-[#555] mt-1">{hint}</p>
      </div>
    );
  }

  const scoutAction = async (appId, action) => {
    try {
      await api.post(`/admin/scout-applications/${appId}/${action}`);
      toast.success(`Application ${action}d`);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const bulkAction = async (action) => {
    if (selected.size === 0) { toast.error("Select profiles first"); return; }
    try {
      await api.post("/admin/profiles/bulk-action", { user_ids: [...selected], action });
      toast.success(`Applied to ${selected.size} profiles`);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk action failed"); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });
  };

  const toggleSelectAll = () => {
    if (selected.size === profiles.length) setSelected(new Set());
    else setSelected(new Set(profiles.map(p => p.user_id)));
  };

  const filteredScouts = scoutSearch ? scoutApps.filter(a => a.name?.toLowerCase().includes(scoutSearch.toLowerCase()) || a.email?.toLowerCase().includes(scoutSearch.toLowerCase())) : scoutApps;

  return (
    <div className="bg-[#0A0A0A] min-h-screen py-12" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="overline mb-2">ADMINISTRATION</div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter uppercase text-[#F5F5F5]">ADMIN DASHBOARD</h1>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 mb-6 border-b border-[#262626]" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-wider border-b-2 transition-all ${tab === t.id ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-[#A3A3A3] hover:text-[#F5F5F5]"}`} data-testid={`admin-tab-${t.id}`}>
              <t.icon size={16} strokeWidth={1.5} />{t.label}
            </button>
          ))}
        </div>

        {/* USERS TAB */}
        {tab === "users" && (
          <div data-testid="admin-users-table">
            <div className="flex flex-wrap gap-2 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
                <input type="text" value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="input-dark text-sm py-2 pl-9 w-full" placeholder="Search name or email..." data-testid="admin-user-search" />
              </div>
              <select value={userRole} onChange={(e) => setUserRole(e.target.value)} className="input-dark text-sm py-2" data-testid="admin-user-role-filter">
                <option value="">All Roles</option>
                <option value="player">Player</option>
                <option value="scout">Scout</option>
                <option value="admin">Admin</option>
              </select>
              <select value={userPlan} onChange={(e) => setUserPlan(e.target.value)} className="input-dark text-sm py-2" data-testid="admin-user-plan-filter">
                <option value="">All Plans</option>
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
              </select>
              <select value={userCountry} onChange={(e) => setUserCountry(e.target.value)} className="input-dark text-sm py-2 max-w-[180px]" data-testid="admin-user-country-filter">
                <option value="">All Countries</option>
                {countries.map((c) => <option key={c.code} value={c.name}>{c.name}</option>)}
              </select>
              <button onClick={exportUsersCsv} className="btn-outline text-xs px-3 py-2 flex items-center gap-1.5" data-testid="admin-export-csv-btn">
                <Download size={12} /> CSV
              </button>
              {currentAdmin?.is_super_admin && (
                <button onClick={sendBlast} disabled={blastSending} className="bg-[#D4AF37] text-[#0A0A0A] font-bold uppercase tracking-wider text-[10px] px-3 py-2 rounded-sm hover:bg-[#C5A059] flex items-center gap-1.5 disabled:opacity-50" data-testid="admin-send-blast-btn">
                  <Send size={11} /> {blastSending ? "SENDING..." : "Send Announcement"}
                </button>
              )}
            </div>
            {loading ? <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div> : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#262626]">
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#A3A3A3]"><SortHeader col="email">EMAIL</SortHeader></th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#A3A3A3]"><SortHeader col="name">NAME</SortHeader></th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#A3A3A3]"><SortHeader col="role">ROLE</SortHeader></th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#A3A3A3]"><SortHeader col="plan">PLAN</SortHeader></th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#A3A3A3]"><SortHeader col="country">COUNTRY</SortHeader></th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#A3A3A3]">PHONE</th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#A3A3A3]"><SortHeader col="created_at">JOINED</SortHeader></th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#A3A3A3]">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u._id} className="border-b border-[#262626] hover:bg-[#141414] transition-colors">
                        <td className="py-3 px-4 text-sm text-[#E5E5E5]">{u.email}</td>
                        <td className="py-3 px-4 text-sm text-[#E5E5E5]">{u.name || "-"}</td>
                        <td className="py-3 px-4 text-sm"><span className={u.role === "admin" ? "text-[#D4AF37]" : "text-[#A3A3A3]"}>{u.role}</span></td>
                        <td className="py-3 px-4">
                          <select value={u.plan || ""} onChange={(e) => changeUserPlan(u._id, e.target.value || null)} className="bg-[#1A1A1A] border border-[#262626] text-[#E5E5E5] text-xs px-2 py-1 rounded-sm" data-testid={`plan-select-${u._id}`}>
                            <option value="">None</option>
                            <option value="standard">Basic</option>
                            <option value="pro">Full</option>
                            <option value="elite">Elite</option>
                          </select>
                        </td>
                        <td className="py-3 px-4 text-sm text-[#A3A3A3]">{u.country || "-"}</td>
                        <td className="py-3 px-4 text-xs text-[#A3A3A3]">
                          {u.phone ? (
                            <a href={`https://wa.me/${u.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline" data-testid={`admin-phone-${u._id}`} title="Open in WhatsApp">{u.phone}</a>
                          ) : (
                            <span className="text-[#444]">—</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-[#A3A3A3]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1.5">
                            <Link to={`/admin/edit/${u._id}`} className="btn-outline text-[9px] px-2.5 py-1 flex items-center gap-1 w-fit" data-testid={`admin-edit-user-${u._id}`}><Pencil size={9} />Edit</Link>
                            {currentAdmin?.is_super_admin && u._id !== currentAdmin?.id && (
                              <button onClick={() => impersonateUser(u._id, u.email)} className="text-[9px] px-2.5 py-1 flex items-center gap-1 bg-[#181207] border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm" data-testid={`admin-login-as-${u._id}`}>
                                <LogIn size={9} /> Login As
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {users.length === 0 && <p className="text-center py-10 text-[#A3A3A3]">No users found</p>}
                <div className="text-xs text-[#555] mt-2">{users.length} user{users.length !== 1 ? "s" : ""}</div>
              </div>
            )}
          </div>
        )}

        {/* PROFILES TAB */}
        {tab === "nurture" && (
          <div data-testid="admin-nurture-tab">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] ${nurtureStats?.scheduler_running ? "bg-[#0F2A0F] text-[#7FD17F] border border-[#7FD17F]/30" : "bg-[#2A0F0F] text-[#FF7F7F] border border-[#FF7F7F]/30"}`} data-testid="scheduler-status">
                  <span className={`w-1.5 h-1.5 rounded-full ${nurtureStats?.scheduler_running ? "bg-[#7FD17F] animate-pulse" : "bg-[#FF7F7F]"}`} />
                  {nurtureStats?.scheduler_running ? "Scheduler Live" : "Scheduler Stopped"}
                </span>
                <span className="text-[10px] text-[#666] tracking-wide">Ticks every 15 minutes</span>
              </div>
              {currentAdmin?.is_super_admin && (
                <button onClick={runNurtureNow} disabled={nurtureRunning} className="bg-[#D4AF37] text-[#0A0A0A] font-bold uppercase tracking-wider text-[10px] px-3 py-2 rounded-sm hover:bg-[#C5A059] flex items-center gap-1.5 disabled:opacity-50" data-testid="nurture-run-now-btn">
                  <Play size={11} /> {nurtureRunning ? "RUNNING..." : "Run Tick Now"}
                </button>
              )}
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <KpiCard label="In sequence" value={nurtureStats?.in_sequence ?? "—"} hint="Unpaid + active" testId="kpi-in-sequence" />
              <KpiCard label="Sent 24h" value={nurtureStats?.sent_last_24h ?? "—"} hint="Last 24 hours" testId="kpi-sent-24h" />
              <KpiCard label="Sent total" value={nurtureStats?.sent_total ?? "—"} hint="All time" testId="kpi-sent-total" />
              <KpiCard label="Unsubscribed" value={nurtureStats?.unsubscribed ?? "—"} hint="Opted out" testId="kpi-unsubscribed" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Recent sends */}
              <div className="bg-[#0E0E0E] border border-[#1A1A1A] p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Recent Sends</h3>
                  <span className="text-[10px] text-[#666]">last 20</span>
                </div>
                {!nurtureStats || nurtureStats.recent_sends.length === 0 ? (
                  <p className="text-xs text-[#555] py-8 text-center">No emails sent yet — they'll appear here as soon as the next tick fires.</p>
                ) : (
                  <ul className="space-y-2 max-h-[420px] overflow-y-auto" data-testid="nurture-recent-list">
                    {nurtureStats.recent_sends.map((r, i) => (
                      <li key={i} className="flex items-center justify-between gap-3 py-2 px-2 border-b border-[#1A1A1A] last:border-0">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-[#E5E5E5] truncate">{r.email}</p>
                          <p className="text-[10px] text-[#666] truncate">{STEP_LABELS[r.step] || `Step ${r.step + 1}`} · {r.country || "—"}</p>
                        </div>
                        <span className="text-[10px] text-[#888] tracking-wide whitespace-nowrap">{fmtRelative(r.sent_at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Upcoming sends */}
              <div className="bg-[#0E0E0E] border border-[#1A1A1A] p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Next 24h Queue</h3>
                  <span className="text-[10px] text-[#666]">{nurtureStats?.upcoming_24h ?? 0} scheduled</span>
                </div>
                {!nurtureStats || nurtureStats.upcoming_sends.length === 0 ? (
                  <p className="text-xs text-[#555] py-8 text-center">Nothing scheduled in the next 24h. New signups will appear here ~24h after they register.</p>
                ) : (
                  <ul className="space-y-2 max-h-[420px] overflow-y-auto" data-testid="nurture-upcoming-list">
                    {nurtureStats.upcoming_sends.map((u, i) => (
                      <li key={i} className="flex items-center justify-between gap-3 py-2 px-2 border-b border-[#1A1A1A] last:border-0">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-[#E5E5E5] truncate">{u.email}</p>
                          <p className="text-[10px] text-[#666] truncate">{STEP_LABELS[u.step] || `Step ${u.step + 1}`} · {u.country || "—"}</p>
                        </div>
                        <span className="inline-flex items-center gap-1 text-[10px] text-[#D4AF37] tracking-wide whitespace-nowrap">
                          <Clock size={10} /> {fmtRelative(u.next_send_at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <p className="text-[10px] text-[#444] mt-5 leading-relaxed">
              Players auto-enter the sequence on registration. Each email is personalized by GPT-5.2 using their name, position and country.
              Sequence pauses immediately when a user pays. Users can unsubscribe via the link in every email.
            </p>
          </div>
        )}

        {/* PROFILES TAB */}
        {tab === "profiles" && (
          <div data-testid="admin-profiles-list">
            <div className="flex flex-wrap gap-2 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
                <input type="text" value={profileSearch} onChange={(e) => setProfileSearch(e.target.value)} className="input-dark text-sm py-2 pl-9 w-full" placeholder="Search player name..." data-testid="admin-profile-search" />
              </div>
              <select value={profileStatus} onChange={(e) => setProfileStatus(e.target.value)} className="input-dark text-sm py-2" data-testid="admin-profile-status-filter">
                <option value="">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="verified">Verified</option>
                <option value="featured">Featured</option>
                <option value="circulation">Circulation</option>
              </select>
            </div>

            {/* Bulk actions bar */}
            {selected.size > 0 && (
              <div className="bg-[#1A1A1A] border border-[#262626] px-4 py-2.5 rounded-sm mb-4 flex items-center gap-3 flex-wrap" data-testid="bulk-actions-bar">
                <span className="text-xs text-[#D4AF37] font-bold">{selected.size} selected</span>
                <div className="flex gap-1.5">
                  {[
                    { label: "Verify", action: "verify" },
                    { label: "Unverify", action: "unverify" },
                    { label: "Feature", action: "feature" },
                    { label: "Unfeature", action: "unfeature" },
                    { label: "Publish", action: "publish" },
                    { label: "Unpublish", action: "unpublish" },
                    { label: "Circulation", action: "circulation_on" },
                  ].map(b => (
                    <button key={b.action} onClick={() => bulkAction(b.action)} className="btn-outline text-[9px] px-2.5 py-1" data-testid={`bulk-${b.action}`}>{b.label}</button>
                  ))}
                </div>
                <button onClick={() => setSelected(new Set())} className="text-[9px] text-[#666] hover:text-[#F5F5F5] ml-auto">Clear</button>
              </div>
            )}

            {loading ? <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div> : (
              <div className="space-y-3">
                {/* Select all */}
                <div className="flex items-center gap-2 mb-2">
                  <button onClick={toggleSelectAll} className="text-[#666] hover:text-[#D4AF37] transition-colors" data-testid="select-all">
                    {selected.size === profiles.length && profiles.length > 0 ? <CheckSquare size={14} /> : <Square size={14} />}
                  </button>
                  <span className="text-[10px] text-[#666]">Select all ({profiles.length})</span>
                </div>
                {profiles.length === 0 && <p className="text-center py-10 text-[#A3A3A3]">No profiles found</p>}
                {profiles.map((p) => (
                  <div key={p._id} className={`card-dark p-5 ${selected.has(p.user_id) ? "border-[#D4AF37]/40" : ""}`}>
                    <div className="flex items-start gap-3">
                      <button onClick={() => toggleSelect(p.user_id)} className="text-[#666] hover:text-[#D4AF37] transition-colors mt-1 flex-shrink-0" data-testid={`select-${p.user_id}`}>
                        {selected.has(p.user_id) ? <CheckSquare size={14} className="text-[#D4AF37]" /> : <Square size={14} />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="font-bold text-[#F5F5F5] uppercase tracking-tight text-sm">{p.name || "Unnamed"}</h3>
                          <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${p.status === "published" ? "bg-green-400/10 text-green-400" : "bg-yellow-400/10 text-yellow-400"}`}>{p.status || "draft"}</span>
                          {p.is_reviewed && <span className="text-[9px] font-bold text-blue-400">VERIFIED</span>}
                          {p.is_featured && <span className="text-[9px] font-bold text-[#D4AF37]">FEATURED</span>}
                          {p.is_circulation_candidate && <span className="text-[9px] font-bold text-purple-400">CIRCULATION</span>}
                        </div>
                        <div className="text-[10px] text-[#666]">ID: {p.user_id}</div>
                        {p.slug && (
                          <a href={`/player/${p.slug}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#D4AF37] hover:underline inline-flex items-center gap-1 mt-0.5"><ExternalLink size={9} />/player/{p.slug}</a>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5 flex-shrink-0">
                        {p.slug && (
                          <a href={`/player/${p.slug}`} target="_blank" rel="noopener noreferrer" className="btn-outline text-[9px] px-2.5 py-1 flex items-center gap-1" data-testid={`preview-${p.user_id}`}><Eye size={10} />View</a>
                        )}
                        {p.status === "published" ? (
                          <button onClick={() => adminAction("unpublish", p.user_id)} className="btn-outline text-[9px] px-2.5 py-1 flex items-center gap-1" data-testid={`unpublish-${p.user_id}`}><EyeOff size={10} />Unpublish</button>
                        ) : (
                          <button onClick={() => adminAction("publish", p.user_id)} className="btn-outline text-[9px] px-2.5 py-1 flex items-center gap-1" data-testid={`publish-${p.user_id}`}><Eye size={10} />Publish</button>
                        )}
                        <button onClick={() => adminAction(p.is_featured ? "feature" : "feature", p.user_id)} className="btn-outline text-[9px] px-2.5 py-1 flex items-center gap-1" data-testid={`feature-${p.user_id}`}>
                          {p.is_featured ? <><StarOff size={10} />Unfeature</> : <><Star size={10} />Feature</>}
                        </button>
                        <button onClick={() => adminAction(p.is_reviewed ? "unverify" : "review", p.user_id)} className="btn-outline text-[9px] px-2.5 py-1 flex items-center gap-1" data-testid={`verify-${p.user_id}`}>
                          {p.is_reviewed ? <><XCircle size={10} />Unverify</> : <><CheckCircle size={10} />Verify</>}
                        </button>
                        <button onClick={() => adminAction("circulation", p.user_id)} className={`btn-outline text-[9px] px-2.5 py-1 flex items-center gap-1 ${p.is_circulation_candidate ? "border-purple-400/40 text-purple-400" : ""}`} data-testid={`circulation-${p.user_id}`}>
                          <RefreshCw size={10} />{p.is_circulation_candidate ? "Remove" : "Circ."}
                        </button>
                        <Link to={`/admin/edit/${p.user_id}`} className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] font-bold text-[9px] px-2.5 py-1 rounded-sm hover:bg-[#D4AF37]/20 transition-colors flex items-center gap-1" data-testid={`admin-edit-profile-${p.user_id}`}>
                          <Pencil size={10} />Edit as Admin
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* SCOUTS TAB */}
        {tab === "scouts" && (
          <div data-testid="admin-scouts-list">
            <div className="mb-4">
              <div className="relative max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
                <input type="text" value={scoutSearch} onChange={(e) => setScoutSearch(e.target.value)} className="input-dark text-sm py-2 pl-9 w-full" placeholder="Search scout name or email..." data-testid="admin-scout-search" />
              </div>
            </div>
            {loading ? <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div> : (
              <div className="space-y-3">
                {filteredScouts.length === 0 && <p className="text-center py-10 text-[#A3A3A3]">No scout applications</p>}
                {filteredScouts.map((app, i) => (
                  <div key={i} className="card-dark p-5">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div>
                        <div className="font-bold text-[#F5F5F5] text-sm">{app.name}</div>
                        <div className="text-xs text-[#A3A3A3] mt-0.5">{app.role_title} at {app.organisation}</div>
                        <div className="text-xs text-[#A3A3A3]">{app.email} &middot; {app.country}</div>
                        <p className="text-sm text-[#888] mt-2">{app.description}</p>
                        <span className={`text-xs font-bold uppercase tracking-wider mt-2 inline-block ${app.status === "approved" ? "text-green-400" : app.status === "rejected" ? "text-red-400" : "text-yellow-400"}`}>{app.status}</span>
                      </div>
                      {app.status === "pending" && (
                        <div className="flex gap-2 flex-shrink-0">
                          <button onClick={() => scoutAction(app.id, "approve")} className="bg-green-500/20 border border-green-500/30 text-green-400 font-bold text-xs px-4 py-2 rounded-sm hover:bg-green-500/30 transition-colors" data-testid={`approve-${app.id}`}>APPROVE</button>
                          <button onClick={() => scoutAction(app.id, "reject")} className="bg-red-500/20 border border-red-500/30 text-red-400 font-bold text-xs px-4 py-2 rounded-sm hover:bg-red-500/30 transition-colors" data-testid={`reject-${app.id}`}>REJECT</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ENQUIRIES TAB */}
        {tab === "enquiries" && (
          <div data-testid="admin-enquiries-list">
            {loading ? <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div> : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs text-[#A3A3A3]">{enquiries.length} enquir{enquiries.length !== 1 ? "ies" : "y"}</span>
                  {enquiries.length > 0 && (
                    <button onClick={async () => {
                      try { const response = await api.get("/admin/enquiries/export", { responseType: "blob" }); const blob = new Blob([response.data], { type: "text/csv" }); const url = window.URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "enquiries_export.csv"; a.click(); window.URL.revokeObjectURL(url); toast.success("CSV exported"); } catch { toast.error("Export failed"); }
                    }} className="btn-outline text-xs px-3 py-1.5 flex items-center gap-1.5" data-testid="export-enquiries-csv"><Download size={12} /> EXPORT CSV</button>
                  )}
                </div>
                <div className="space-y-3">
                  {enquiries.length === 0 && <p className="text-center py-10 text-[#A3A3A3]">No enquiries yet</p>}
                  {enquiries.map((enq, i) => (
                    <div key={i} className="card-dark p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="font-bold text-[#F5F5F5] text-sm">{enq.scout_name} <span className="text-[#A3A3A3] font-normal">from</span> {enq.club}</div>
                          <div className="text-xs text-[#A3A3A3] mt-1">To: {enq.player_name} &middot; {enq.email}</div>
                          <p className="text-sm text-[#E5E5E5] mt-2">{enq.message}</p>
                        </div>
                        <div className="text-xs text-[#A3A3A3] flex-shrink-0">{enq.created_at ? new Date(enq.created_at).toLocaleDateString() : ""}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* PAYMENTS TAB */}
        {tab === "payments" && (
          <div data-testid="admin-payments-table">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <div className="grid grid-cols-3 gap-2 flex-1">
                <div className="bg-[#0E0E0E] border border-[#1A1A1A] p-3 rounded-sm">
                  <p className="text-[9px] text-[#666] uppercase tracking-wider mb-1">Total</p>
                  <p className="text-xl font-black text-[#E5E5E5] tabular-nums">{paymentStats?.total ?? "—"}</p>
                </div>
                <div className="bg-[#0E0E0E] border border-green-500/20 p-3 rounded-sm">
                  <p className="text-[9px] text-[#666] uppercase tracking-wider mb-1">Paid</p>
                  <p className="text-xl font-black text-green-400 tabular-nums">{paymentStats?.paid ?? "—"}</p>
                </div>
                <div className="bg-[#0E0E0E] border border-yellow-500/20 p-3 rounded-sm">
                  <p className="text-[9px] text-[#666] uppercase tracking-wider mb-1">Pending</p>
                  <p className="text-xl font-black text-yellow-400 tabular-nums">{paymentStats?.pending ?? "—"}</p>
                </div>
              </div>
              {currentAdmin?.is_super_admin && (
                <button onClick={reconcilePayments} disabled={reconciling} className="bg-[#D4AF37] text-[#0A0A0A] font-bold uppercase tracking-wider text-[10px] px-4 py-3 rounded-sm hover:bg-[#C5A059] flex items-center gap-1.5 disabled:opacity-50" data-testid="payments-reconcile-btn">
                  <RefreshCw size={11} className={reconciling ? "animate-spin" : ""} /> {reconciling ? "Syncing from Stripe..." : "Sync from Stripe"}
                </button>
              )}
            </div>
            <p className="text-[11px] text-[#666] mb-3 leading-relaxed">
              "Pending" means our DB hasn't received confirmation. Click <span className="text-[#D4AF37] font-semibold">Sync from Stripe</span> to query Stripe for each pending session and reconcile — this recovers payments that actually completed but never updated our DB (e.g. if the user closed the browser before reaching the welcome page).
            </p>
            {loading ? <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div> : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#262626]">
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#A3A3A3]">USER</th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#A3A3A3]">COUNTRY</th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#A3A3A3]">AMOUNT</th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#A3A3A3]">STATUS</th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#A3A3A3]">CREATED</th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#A3A3A3]">STRIPE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((tx, i) => (
                      <tr key={i} className="border-b border-[#262626] hover:bg-[#141414] transition-colors">
                        <td className="py-3 px-4 text-xs">
                          <div className="text-[#E5E5E5]">{tx.user_email || <span className="text-[#555]">(unknown)</span>}</div>
                          {tx.user_name && <div className="text-[10px] text-[#777]">{tx.user_name}</div>}
                          {tx.user_phone && <a href={`https://wa.me/${tx.user_phone.replace(/[^0-9]/g,"")}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#D4AF37] hover:underline">{tx.user_phone}</a>}
                        </td>
                        <td className="py-3 px-4 text-xs text-[#A3A3A3]">{tx.user_country || "—"}</td>
                        <td className="py-3 px-4 text-sm text-[#E5E5E5]">${tx.amount}</td>
                        <td className="py-3 px-4">
                          <span className={`font-bold uppercase text-xs ${tx.payment_status === "paid" ? "text-green-400" : tx.payment_status === "expired" ? "text-[#777]" : "text-yellow-400"}`}>
                            {tx.payment_status}
                          </span>
                          {tx.reconciled_at && <div className="text-[9px] text-[#666] mt-0.5">✓ synced</div>}
                        </td>
                        <td className="py-3 px-4 text-xs text-[#A3A3A3]">{tx.created_at ? new Date(tx.created_at).toLocaleString() : "-"}</td>
                        <td className="py-3 px-4 text-xs">
                          {tx.session_id && (
                            <a href={`https://dashboard.stripe.com/payments/${tx.session_id}`} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline inline-flex items-center gap-1" title="Open in Stripe Dashboard">
                              <ExternalLink size={10} /> view
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {payments.length === 0 && <p className="text-center py-10 text-[#A3A3A3]">No payments recorded</p>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
